/**
 * ImageUploader
 * ----
 * Handles image upload/delete for MCQ questions and options.
 * Uploads immediately on file selection (no intermediate confirm step).
 * Uses direct axios calls for upload (FormData requires platform-specific
 * file handling that Orval-generated hooks don't account for).
 */

import React, { useState } from 'react';
import {
  Box,
  Pressable,
  Button,
  ButtonText,
  Icon,
  Image,
  Text,
  HStack,
  Spinner,
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  ButtonGroup,
} from '@gluestack-ui/themed';
import { Platform } from 'react-native';
import { X } from 'lucide-react-native';
import * as DocumentPicker from 'expo-document-picker';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getGetTasksByModuleVersionQueryKey } from '@/common/api/endpoints/tasks/tasks';
import {
  useDeleteMcqQuestionImage,
  useDeleteMcqOptionImage,
} from '@/common/api/endpoints/mcq/mcq';

/**
 * Convert a DocumentPicker asset to a FormData-compatible file value.
 * Web: resolve URI to a real File/Blob object.
 * Native: RN's FormData polyfill handles {uri, type, name} objects.
 */
const prepareFile = async (
  asset: DocumentPicker.DocumentPickerAsset,
): Promise<Blob> => {
  if (Platform.OS === 'web') {
    if (asset.file) {
      return new File([asset.file], asset.name, { type: asset.file.type });
    }
    // Fallback: fetch the blob from the URI (data: or blob: URL)
    const response = await fetch(asset.uri);
    const blob = await response.blob();
    return new File([blob], asset.name, { type: asset.mimeType || 'image/jpeg' });
  }
  return {
    uri: asset.uri,
    type: asset.mimeType || 'image/jpeg',
    name: asset.name,
  } as unknown as Blob;
};

type ImageUploaderProps = {
  moduleId: string;
  version: string;
  taskId: string;
  questionId: string;
  optionId?: string;
  type: 'question' | 'option';
  currentImageUrl?: string;
};

/**
 * State flows via query cache: upload/delete mutate the server, then
 * invalidate the task query. The parent re-derives imageUrl from
 * the refreshed cache - no callbacks needed.
 */
export const ImageUploader: React.FC<ImageUploaderProps> = ({
  moduleId,
  version,
  taskId,
  questionId,
  optionId,
  type,
  currentImageUrl,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [validationError, setValidationError] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Direct upload mutation - bypasses Orval to handle platform-specific FormData
  const uploadMutation = useMutation({
    mutationFn: async (asset: DocumentPicker.DocumentPickerAsset) => {
      const file = await prepareFile(asset);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('alt_text', '');

      const basePath = `/modules/${moduleId}/versions/${version}/tasks/${taskId}/mcq`;
      const url = type === 'question'
        ? `${basePath}/questions/${questionId}/image`
        : `${basePath}/questions/${questionId}/options/${optionId}/image`;

      return axios.post(url, formData);
    },
  });

  const deleteQuestionMutation = useDeleteMcqQuestionImage();
  const deleteOptionMutation = useDeleteMcqOptionImage();

  const invalidateTaskQueries = () => {
    queryClient.invalidateQueries({ queryKey: getGetTasksByModuleVersionQueryKey(moduleId, version) });
  };

  /** Pick a file and upload immediately. */
  const handlePickAndUpload = async () => {
    try {
      setValidationError('');
      const result = await DocumentPicker.getDocumentAsync({
        type: ['image/jpeg', 'image/png'],
        copyToCacheDirectory: true,
      });

      if (result.canceled || !result.assets?.length) return;
      const asset = result.assets[0];

      if (!asset.mimeType?.startsWith('image/')
          || (!asset.name.toLowerCase().endsWith('.jpg')
           && !asset.name.toLowerCase().endsWith('.jpeg')
           && !asset.name.toLowerCase().endsWith('.png'))) {
        setValidationError(t('lecturer.moduleEditor.taskEditor.mcq.imageUpload.invalidFormat'));
        return;
      }

      if (asset.size && asset.size > 5 * 1024 * 1024) {
        setValidationError(t('lecturer.moduleEditor.taskEditor.mcq.imageUpload.fileTooLarge'));
        return;
      }

      uploadMutation.mutate(asset, {
        onSuccess: () => invalidateTaskQueries(),
        onError: () => {
          setValidationError(t('lecturer.moduleEditor.taskEditor.mcq.imageUpload.uploadFailed'));
        },
      });
    } catch {
      setValidationError(t('lecturer.moduleEditor.taskEditor.mcq.imageUpload.selectFailed'));
    }
  };

  const handleDelete = () => {
    const callbacks = {
      onSuccess: () => {
        setShowDeleteDialog(false);
        invalidateTaskQueries();
      },
    };

    if (type === 'question') {
      deleteQuestionMutation.mutate({
        moduleId, version, taskId, questionId,
      }, callbacks);
    } else {
      deleteOptionMutation.mutate({
        moduleId, version, taskId, questionId, optionId: optionId!,
      }, callbacks);
    }
  };

  // Image exists: show preview with overlaid delete button
  if (currentImageUrl) {
    return (
      <Box>
        <Box position="relative" alignSelf="flex-start">
          <Image
            testID="image-preview"
            source={{ uri: currentImageUrl }}
            alt="Uploaded image"
            width={140}
            height={100}
            borderRadius={8}
            style={{ resizeMode: 'cover' }}
          />
          <Pressable
            testID="delete-image-button"
            position="absolute"
            top={4}
            right={4}
            bg="$bgDark"
            borderRadius="$full"
            p="$1"
            opacity={0.85}
            onPress={() => setShowDeleteDialog(true)}
          >
            <Icon as={X} size="xs" color="$textOnDark" />
          </Pressable>
        </Box>

        <AlertDialog isOpen={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
          <AlertDialogBackdrop />
          <AlertDialogContent testID="delete-confirmation-dialog">
            <AlertDialogHeader>
              <Text>{t('lecturer.moduleEditor.taskEditor.mcq.imageUpload.confirmDeleteTitle')}</Text>
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text>{t('lecturer.moduleEditor.taskEditor.mcq.imageUpload.confirmDeleteBody')}</Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button variant="outline" onPress={() => setShowDeleteDialog(false)}>
                  <ButtonText>{t('common.buttons.cancel')}</ButtonText>
                </Button>
                <Button testID="confirm-delete-button" action="negative" onPress={handleDelete}>
                  <ButtonText>{t('common.buttons.delete')}</ButtonText>
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Box>
    );
  }

  // No image: upload button (or spinner while uploading)
  return (
    <Box>
      {uploadMutation.isPending ? (
        <HStack space="sm" alignItems="center" testID="upload-loading-indicator">
          <Spinner size="small" />
          <Text size="xs">{t('lecturer.moduleEditor.taskEditor.mcq.imageUpload.uploading')}</Text>
        </HStack>
      ) : (
        <Button
          testID="image-upload-button"
          variant="outline"
          onPress={handlePickAndUpload}
          size="xs"
          alignSelf="flex-start"
        >
          <ButtonText>{t('lecturer.moduleEditor.taskEditor.mcq.imageUpload.upload')}</ButtonText>
        </Button>
      )}
      {!!validationError && (
        <Text testID="validation-error" size="xs" color="$statusError">{validationError}</Text>
      )}
    </Box>
  );
};
